import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
  Avatar,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ErrorHandler from '../../components/core/ErrorHandler';
import { Levels } from '../../components/layout/Toast';
import { useUser } from '../../hooks/useUser';
import fileUploadHandler from '../../middleware/UploadFile';
import { deleteUser, forgot } from '../../redux/services/userService';
import useErrorStore from '../../zustand/error';
import { SecuritySettings } from '../../features/mfa/components/SecuritySettings';
import { NotificationSettings } from './notificationSettings';

const locales = {
  nl: 'Dutch',
  en: 'English',
};

function ProfileMainContent() {
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);
  const hiddenFileInput = useRef(null);
  const { t } = useTranslation();
  const userQuery = useUser();

  const navigate = useNavigate();
  const { pushError } = useErrorStore();

  if (!userQuery.isSuccess) {
    return null;
  }

  const user = userQuery.data;

  const resetPassword = async () => {
    if (!user.email) {
      pushError({
        level: Levels.error,
        message: t('profile.no_email_found'),
      });
      return;
    }
    await forgot(user.email);
    pushError({
      level: Levels.success,
      message: t('profile.email_sent'),
    });
  };

  const chooseProfileImage = async (e: any) => {
    const file = e.target.files[0];
    if (file.type.includes('image')) {
      const image = await fileUploadHandler(file);
      setProfileImage(image);
      setError('');
    } else {
      setError(t('profile.profile_image_type'));
    }
  };

  const handleSaveChanges = () => {
    // Implement save changes logic here
    setShowEditForm(false);
  };

  return (
    <Box sx={{ m: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* {showEditForm ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="plain" onClick={() => setShowEditForm(false)}>
              {t('profile.button_Cancel')}
            </Button>
            <Button
              variant="solid"
              startDecorator={<SaveIcon />}
              onClick={handleSaveChanges}
            >
              {t('profile.button_save_changes')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="solid"
              startDecorator={<EditIcon />}
              onClick={() => setShowEditForm(true)}
            >
              {t('profile.button_edit_profile')}
            </Button>
          </Box>
        )} */}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 'md',
            bgcolor: 'background.paper',
            boxShadow: 'sm',
            p: 2,
          }}
        >
          {/* Profile picture and name */}
          <Avatar
            src={profileImage}
            alt="profile"
            sx={{
              width: 128,
              height: 128,
              boxShadow: 1,
              border: 2,
              borderColor: 'background.paper',
            }}
          />
          {showEditForm && (
            <Button
              variant="plain"
              startDecorator={<CameraAltIcon />}
              onClick={() => {
                console.error(`not implemented yet`);
                // hiddenFileInput.current.click()
              }}
            >
              {t('profile.upload_photo')}
            </Button>
          )}

          {/* Personal info */}
          <Stack spacing={2} sx={{ mt: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl>
                <FormLabel>{t('profile.user_profile_first_name')}</FormLabel>
                <Input
                  disabled={!showEditForm}
                  value={user.firstName}
                  // onChange={(e) => setFirstName(e.target.value)}
                />
              </FormControl>
            </Stack>
            {/* <Stack direction="row" spacing={2} alignItems="center">
              <Typography >
                {t('profile.user_profile_last_name')}:
              </Typography>
              <Input
                disabled={!showEditForm}
                value={user?.lastName}
                // onChange={(e) => setLastName(e.target.value)}
              />
            </Stack> */}
            <FormControl>
              <FormLabel>{t('branch')}</FormLabel>
              <Input disabled value={user.branch} />
            </FormControl>
            <FormControl>
              <FormLabel>{t('profile.user_profile_email')}</FormLabel>
              <Input
                disabled={!showEditForm}
                value={user.email}
                // onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
            {/* <Stack direction="row" spacing={2} alignItems="flex-start">
              <Typography sx={{ minWidth: 150, mt: 1 }}>
                {t('profile.user_profile_description')}:
              </Typography>
              <Textarea
                disabled={!showEditForm}
                value={user.}
                // onChange={(e) => setDescription(e.target.value)}
                minRows={3}
                sx={{ width: '100%' }}
              />
            </Stack> */}
            <FormControl>
              <FormLabel>{t('language')}</FormLabel>
              <Select
                disabled={!showEditForm}
                placeholder={t('language')}
                // defaultValue={user.language}
              >
                {Object.entries(locales).map(([code, label]) => (
                  <Option key={code} value={code}>
                    {label}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <input
            type="file"
            ref={hiddenFileInput}
            style={{ display: 'none' }}
            onChange={chooseProfileImage}
          />
        </Box>
        <ErrorHandler error={error} />
      </Box>

      <SecuritySettings mfaEnabled={Boolean(user.mfaEnabled)} />

      <NotificationSettings />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography level="h4">Extra</Typography>

        <Stack gap={1}>
          <Button variant="soft" onClick={() => navigate('/privacy-statement')}>
            Privacy Statement
          </Button>

          <Button variant="soft" onClick={resetPassword}>
            Wachtwoord vergeten
          </Button>

          <Button
            variant="soft"
            color="danger"
            startDecorator={<DeleteIcon />}
            onClick={() => setDeleteAccountModalOpen(true)}
          >
            {t('profile.delete_account')}
          </Button>
        </Stack>

        {/* Delete Account Modal */}
        <Modal
          open={deleteAccountModalOpen}
          onClose={() => setDeleteAccountModalOpen(false)}
        >
          <ModalDialog>
            <Typography level="h4">{t('profile.delete_account')}</Typography>
            <Typography>{t('profile.delete_account_prompt')}</Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 2,
              }}
            >
              <Button
                variant="plain"
                onClick={() => setDeleteAccountModalOpen(false)}
              >
                {t('profile.button_Cancel')}
              </Button>
              <Button
                variant="solid"
                color="danger"
                onClick={async () => {
                  await deleteUser(user._id);
                  navigate('/login');
                }}
              >
                {t('profile.delete_account_confirm')}
              </Button>
            </Box>
          </ModalDialog>
        </Modal>
      </Box>
    </Box>
  );
}

export default ProfileMainContent;
